// lib/slides/annotationLayout.ts
//
// DETERMINISTIC geometry for review callouts - the app, not the model, owns
// placement correctness (AGENTS §4.6 precedent: trust the app for geometry).
// Pure functions, shared by the SLIDES_* command layer and tests:
//
//  - resolveAnchorText: the agent NAMES a label ("ZONE CAISSES"); the exact
//    anchor coordinates come from the page's positioned text map (extracted
//    from the PDF's own text layer at import) - no vision coordinate guessing.
//  - clampFrameToPage / clampAnchorToPage: nothing ever renders off-canvas.
//  - resolveNoteCollisions: bubbles never overlap each other (nor the opaque
//    validation stamp) - a new bubble slides to the nearest free slot.
//  - resolveStampFrame / reservedExportRegions: the one definition of the
//    opaque blocks the exporter paints over a page's annotations (the
//    validation stamp, the corner block), shared by the
//    canvas, the exporter and the collision guard.

import { pageScaleFromA4 } from './noteMetrics';

export interface PageTextItem {
  t: string;
  x: number;
  y: number;
}

export interface Frame {
  x: number;
  y: number;
  w: number;
  h: number;
}

const PAGE_PAD = 8;
const NOTE_GAP = 8;

const normalize = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Resolve a named label to its exact page-point coordinates.
 * Match order: exact → item-contains-query → query-contains-item. Among equal
 * ranks the longest label wins (most specific). Null when nothing matches -
 * the caller falls back to the numeric anchor.
 */
export function resolveAnchorText(
  textMap: ReadonlyArray<PageTextItem> | undefined,
  query: string
): { x: number; y: number } | null {
  if (!textMap || textMap.length === 0) return null;
  const q = normalize(query);
  if (q.length < 2) return null;
  let best: { item: PageTextItem; rank: number } | null = null;
  for (const item of textMap) {
    const t = normalize(item.t);
    if (t.length < 2) continue;
    let rank = 0;
    if (t === q) rank = 3;
    else if (t.includes(q)) rank = 2;
    else if (q.includes(t)) rank = 1;
    if (rank === 0) continue;
    if (
      !best ||
      rank > best.rank ||
      (rank === best.rank && item.t.length > best.item.t.length)
    ) {
      best = { item, rank };
    }
  }
  return best ? { x: best.item.x, y: best.item.y } : null;
}

/** Keep a frame fully inside the page (with padding); oversized frames shrink. */
export function clampFrameToPage(frame: Frame, pageW: number, pageH: number): Frame {
  const w = Math.min(Math.max(20, frame.w), Math.max(20, pageW - PAGE_PAD * 2));
  const h = Math.min(Math.max(16, frame.h), Math.max(16, pageH - PAGE_PAD * 2));
  const x = Math.min(Math.max(PAGE_PAD, frame.x), pageW - PAGE_PAD - w);
  const y = Math.min(Math.max(PAGE_PAD, frame.y), pageH - PAGE_PAD - h);
  return { x, y, w, h };
}

export function clampAnchorToPage(
  anchor: { x: number; y: number },
  pageW: number,
  pageH: number
): { x: number; y: number } {
  return {
    x: Math.min(Math.max(0, anchor.x), pageW),
    y: Math.min(Math.max(0, anchor.y), pageH),
  };
}

const intersects = (a: Frame, b: Frame): boolean =>
  a.x < b.x + b.w + NOTE_GAP &&
  a.x + a.w + NOTE_GAP > b.x &&
  a.y < b.y + b.h + NOTE_GAP &&
  a.y + a.h + NOTE_GAP > b.y;

/**
 * Slide a note frame to the nearest position that does not overlap any blocker
 * (other bubbles, and the opaque validation stamp on the stamp page - a bubble
 * under the stamp is simply invisible in the deliverable).
 *
 * Pass 1 keeps the bubble in its column and slides it vertically (below the
 * blocker first, then above) - that preserves the reviewer's reading order.
 * Pass 2 runs only when the column has no room left, which is what a tall
 * blocker like the stamp causes, and steps sideways instead.
 *
 * When no pass finds a free slot the page is saturated, and the best available
 * answer is the position that covers the FEWEST blockers - never the caller's
 * original, which is the one position already known to collide. Getting that
 * wrong puts the bubble straight back under the opaque stamp, i.e. invisible in
 * the deliverable, which is the whole defect this guard exists to prevent.
 */
export function resolveNoteCollisions(
  frame: Frame,
  existing: ReadonlyArray<Frame>,
  pageW: number,
  pageH: number
): Frame {
  const start = clampFrameToPage(frame, pageW, pageH);
  const blockers = existing.map((f) => clampFrameToPage(f, pageW, pageH));
  const firstHit = (c: Frame): Frame | undefined => blockers.find((b) => intersects(c, b));
  const hitCount = (c: Frame): number => blockers.reduce((n, b) => (intersects(c, b) ? n + 1 : n), 0);

  const slide = (step: (candidate: Frame, hit: Frame) => Frame | null): Frame => {
    let candidate = start;
    for (let iter = 0; iter < blockers.length + 1; iter++) {
      const hit = firstHit(candidate);
      if (!hit) return candidate;
      const next = step(candidate, hit);
      if (!next) break;
      candidate = next;
    }
    return candidate;
  };

  const vertical = slide((candidate, hit) => {
    const below = { ...candidate, y: hit.y + hit.h + NOTE_GAP };
    if (below.y + below.h <= pageH - PAGE_PAD) return below;
    const above = { ...candidate, y: hit.y - NOTE_GAP - candidate.h };
    return above.y >= PAGE_PAD ? above : null;
  });
  if (!firstHit(vertical)) return vertical;

  const horizontal = slide((candidate, hit) => {
    const right = { ...candidate, x: hit.x + hit.w + NOTE_GAP };
    if (right.x + right.w <= pageW - PAGE_PAD) return right;
    const left = { ...candidate, x: hit.x - NOTE_GAP - candidate.w };
    return left.x >= PAGE_PAD ? left : null;
  });
  if (!firstHit(horizontal)) return horizontal;

  // Saturated: least-bad, and always in-bounds (never off-canvas).
  return [vertical, horizontal, start].reduce((bestSoFar, c) =>
    hitCount(c) < hitCount(bestSoFar) ? c : bestSoFar
  );
}

/** `coverage` throughout this file is the share of a bubble sitting on the
 *  page's OCCUPIED region - the drawing closed into a solid area by
 *  app/api/slides/ink-map - not raw ink density. Density read a line drawing as
 *  95% blank and parked comments in the middle of it. */
/** Blank paper: a bubble here hides essentially nothing. */
export const MAX_CLEAR_COVERAGE = 0.04;
/**
 * The worst coverage still worth placing. Above this the bubble is sitting ON
 * the drawing and the page counts as full.
 *
 * There are two bars, not one, because a single strict bar over-corrects. Its
 * first outing refused EVERY callout on a dense section sheet - measured on the
 * We Are Wheelers COUPE ET FACADE page, which reported "no clear space" with
 * only 2 bubbles placed - and a review that refuses to say anything is no more
 * useful than one that is illegible. The user asked for minimal on a crowded
 * sheet, not empty. So: prefer blank paper, accept a lightly-covered slot when
 * that is genuinely the best the page has, and only call the page full when
 * even the best slot would bury the artwork.
 */
export const MAX_TOLERABLE_COVERAGE = 0.75;
/*
 * 0.18 -> 0.32 -> 0.75, measured twice on 2 August against real FEVER reviews.
 *
 * This is ONLY the give-up threshold. chooseClearFrame already returns the
 * least-covered slot the page has, so this number does not decide WHERE a
 * bubble goes - it decides whether the finding is placed at all or thrown away.
 *
 * The second measurement is what forced 0.75. Across one review the best
 * available slots were 45, 48, 58, 59, 69 and 70 percent covered, so 0.32
 * changed nothing and eight findings were lost. Page 6 had only TWO bubbles
 * down and its best remaining slot was already 58%: these A0 technical sheets
 * have essentially no blank paper, and on them the bubble is ~560pt wide at
 * MINIMUM (NOTE_MIN_W scaled ~4x so the text stays readable). Narrowing it does
 * not help either - the text simply wraps taller and the area is the same.
 *
 * So the choice on a dense sheet is binary: a callout sitting on some of the
 * drawing, or no callout. A design review whose whole value is localized
 * findings must choose the former, and the reference RDM decks do exactly that
 * - an opaque bubble in the nearest space with a leader line to the target.
 * Above 75% the bubble would be almost entirely on the drawing and genuinely
 * unreadable, so that still refuses.
 *
 * If callouts start burying artwork, THIS is the number to reconsider, and the
 * honest alternative is a smaller font on dense sheets rather than a stricter
 * bar - a stricter bar does not tidy the page, it deletes the review.
 */
/** Search granularity in page points: fine enough to find margin slots, coarse
 *  enough that a page yields a few thousand candidates rather than a million. */
const LAYOUT_STEP = 10;
/** Normalised distance is worth this much against coverage. Coverage dominates
 *  on purpose - distance only separates slots that are equally clear. Let
 *  distance win and a bubble goes back onto the drawing for being nearer. */
const DISTANCE_WEIGHT = 0.35;

/**
 * Choose where a callout bubble can actually be READ: on blank paper, clear of
 * every blocker, and as near its anchor as those allow.
 *
 * `coverage` reports the share of a rectangle that is drawn content (0..1); the
 * raster side of that lives in components/slides/inkProbe. Keeping the search
 * pure is what makes it testable against a synthetic page.
 *
 * Returns null when NO slot is clear enough - the page has run out of readable
 * room. That is a real answer, not a failure to paper over: the caller turns it
 * into a refusal so the reviewer trims its findings, because placing the bubble
 * anyway is exactly what produces a page of comments stacked over the drawing
 * they annotate.
 */
export function chooseClearFrame(
  size: { w: number; h: number },
  anchor: { x: number; y: number },
  blockers: ReadonlyArray<Frame>,
  pageW: number,
  pageH: number,
  coverage: (frame: Frame) => number
): { frame: Frame; coverage: number } | null {
  const w = Math.min(size.w, pageW - PAGE_PAD * 2);
  const h = Math.min(size.h, pageH - PAGE_PAD * 2);
  const maxX = pageW - PAGE_PAD - w;
  const maxY = pageH - PAGE_PAD - h;
  if (maxX < PAGE_PAD || maxY < PAGE_PAD) return null;

  // The BEST slot is always returned, with what it costs, and the caller decides
  // whether that price is acceptable. Filtering to a threshold in here was the
  // bug: a page with no blank paper produced "nothing found" and the callout was
  // refused, when what the reviewer needed was the least-bad slot plus the fact
  // that the page is getting full.
  const diagonal = Math.hypot(pageW, pageH) || 1;
  let best: Frame | null = null;
  let bestCoverage = 1;
  let bestScore = Infinity;
  for (let y = PAGE_PAD; y <= maxY; y += LAYOUT_STEP) {
    for (let x = PAGE_PAD; x <= maxX; x += LAYOUT_STEP) {
      const candidate = { x, y, w, h };
      // Overlapping another bubble is never acceptable at any price: two stacked
      // comments make BOTH unreadable, where a bubble on light background only
      // dims the drawing under it.
      if (blockers.some((b) => intersects(candidate, b))) continue;
      const cov = coverage(candidate);
      const dist = Math.hypot(x + w / 2 - anchor.x, y + h / 2 - anchor.y) / diagonal;
      const score = cov + dist * DISTANCE_WEIGHT;
      if (score < bestScore) {
        bestScore = score;
        bestCoverage = cov;
        best = candidate;
      }
    }
  }
  return best ? { frame: best, coverage: bestCoverage } : null;
}

/**
 * How many callouts a page should carry - which is NOT how many fit.
 *
 * Those are different limits and conflating them is what produced a page the
 * user rejected as "too much". Greedy packing measured on a real deck offered
 * 12 slots on five of seven sheets and 6 on the dense section sheet, because it
 * will happily ring a drawing with comments crammed into every margin crevice.
 * A human reviewer does not: they write the few things that matter and leave
 * the sheet breathing.
 *
 * So geometry sets the ceiling and density sets the appetite. On a busy sheet
 * (measured: the COUPE ET FACADE page is 66% drawn) the answer is a handful,
 * not a dozen - say the important things on the drawing and the rest in the
 * synthesis.
 */
const EDITORIAL_MAX_NOTES = 6;

/** A typical callout bubble, the probe box every layout budget is measured with.
 *  ONE definition on purpose: the number announced to the agent (LAYOUT BUDGET
 *  in its live deck context) and the number enforced when it places notes are
 *  the same measurement, so they cannot drift apart. */
export const TYPICAL_NOTE = { w: 220, h: 64 };

export function noteBudget(freeSlots: number, occupancy: number): number {
  const appetite =
    occupancy > 0.6 ? 3 : occupancy > 0.45 ? 4 : EDITORIAL_MAX_NOTES;
  return Math.max(0, Math.min(freeSlots, appetite));
}

/**
 * How many more bubbles of a given size still fit on blank paper. A greedy
 * estimate, not exact packing - exact rectangle packing is NP-hard and the
 * answer only has to be honest about the order of magnitude, 8 slots versus 1.
 */
export function countClearSlots(
  size: { w: number; h: number },
  blockers: ReadonlyArray<Frame>,
  pageW: number,
  pageH: number,
  coverage: (frame: Frame) => number,
  limit = 12
): number {
  const taken = [...blockers];
  let n = 0;
  for (; n < limit; n++) {
    const slot = chooseClearFrame(size, { x: pageW / 2, y: pageH / 2 }, taken, pageW, pageH, coverage);
    // Counts only slots that would actually be ACCEPTED, so the budget the
    // reviewer plans against matches what placement will really allow.
    if (!slot || slot.coverage > MAX_TOLERABLE_COVERAGE) break;
    taken.push(slot.frame);
  }
  return n;
}

const CORNER_STAMP_MARGIN_RATIO = 0.02;
const CORNER_STAMP_WIDTH_RATIO = 0.24;
const CORNER_STAMP_MAX_W = 230;
const CORNER_STAMP_H = 66;

/** The top-left band a counter-proposal sheet keeps clear above its image (proposalSheetLayout),
 *  in page points with a top-left origin, scaled with the sheet. */
export function cornerStampFrame(pageW: number, pageH: number): Frame {
  const s = pageScaleFromA4(pageW, pageH);
  return {
    x: pageW * CORNER_STAMP_MARGIN_RATIO,
    y: pageW * CORNER_STAMP_MARGIN_RATIO,
    w: Math.min(CORNER_STAMP_MAX_W * s, pageW * CORNER_STAMP_WIDTH_RATIO),
    h: CORNER_STAMP_H * s,
  };
}

/**
 * Every opaque rectangle the exporter paints ON TOP of a page's annotations, in
 * page points with a top-left origin - the regions a callout bubble must stay
 * out of or it simply will not exist in the deliverable. Only page 1 has one:
 * the validation card, drawn after the page's elements, so a bubble under it is
 * painted over.
 */
export function reservedExportRegions(
  stamp: { x?: number; y?: number; w?: number; h?: number } | null | undefined,
  pageIndex: number,
  pageW: number,
  pageH: number
): Frame[] {
  return pageIndex === 0 ? [resolveStampFrame(stamp, pageW)] : [];
}

/** Default validation-stamp geometry, in page points: a top-left card at a 3%
 *  margin, 46% of the page width, 1.16× as tall as it is wide. */
const STAMP_DEFAULT_MARGIN_RATIO = 0.03;
const STAMP_DEFAULT_WIDTH_RATIO = 0.46;
const STAMP_DEFAULT_ASPECT = 1.16;

/**
 * Where the validation stamp sits on the stamp page (page 1), in page points:
 * the frame the reviewer dragged/resized, or the default card.
 *
 * Single definition on purpose. The canvas draws the stamp here, the exporter
 * draws it here, and the callout layout guard treats it as a blocker here - if
 * the three ever disagree, annotations end up hidden under an opaque card in
 * the exported deliverable.
 */
export function resolveStampFrame(
  stamp: { x?: number; y?: number; w?: number; h?: number } | null | undefined,
  pageW: number
): Frame {
  const n = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
  if (stamp && n(stamp.x) && n(stamp.y) && n(stamp.w) && n(stamp.h)) {
    return { x: stamp.x, y: stamp.y, w: stamp.w, h: stamp.h };
  }
  const w = pageW * STAMP_DEFAULT_WIDTH_RATIO;
  return {
    x: pageW * STAMP_DEFAULT_MARGIN_RATIO,
    y: pageW * STAMP_DEFAULT_MARGIN_RATIO,
    w,
    h: w * STAMP_DEFAULT_ASPECT,
  };
}
